import * as XLSX from 'xlsx-js-style';
import type { ProdRow } from '../components/OSProductivityTable';
import type { MachineOption } from '../select/MachineSelect';
import type { SubprocessOption } from '../select/SubprocessCells';
import { parseDecimalInput } from './decimalInput';
import { RAZMER_STAGE_ID } from '../components/OSStageTree';
import { orderedMachineIds } from './logbookStage';
import { minutesToHoursText } from './timeUnits';

const RAZMER_LABEL = 'Размерување';

interface ExportArgs {
  rows: ProdRow[];
  subprocesses: SubprocessOption[];
  machines: MachineOption[];
  /** Machines of the Размерување process definition (information column only). */
  razmerMachineIds?: string[];
  mainIdent?: string;
  mainName?: string;
}

const headerStyle = {
  fill: { fgColor: { rgb: 'D9D9D9' } },
  font: { bold: true, color: { rgb: '000000' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: '999999' } },
    bottom: { style: 'thin', color: { rgb: '999999' } },
    left: { style: 'thin', color: { rgb: '999999' } },
    right: { style: 'thin', color: { rgb: '999999' } },
  },
};

const cellStyle = {
  alignment: { vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: 'DDDDDD' } },
    bottom: { style: 'thin', color: { rgb: 'DDDDDD' } },
    left: { style: 'thin', color: { rgb: 'DDDDDD' } },
    right: { style: 'thin', color: { rgb: 'DDDDDD' } },
  },
};

const safeFilenamePart = (value?: string) =>
  String(value || 'normativ')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80);

const num = (value: unknown): number => parseDecimalInput(value) ?? 0;

// Times are STORED in minutes and written as hours:minutes „0:45“ with ЕМ „h:mm“ (decision
// 2026-10-07, same as the server export) — the server import reads them back by the ЕМ cell.
// Header text must match the server (OS_PRODUCTIVITY_HEADERS_MK in server/routes/ingredients.js).
const TIME_UNIT_TEXT = 'h:mm';

// Setup params go one per line in a single cell, exactly as the server-side „Вкупно Log Book“
// export writes them — the import splits on the newline. Without these two columns, importing
// this file wiped the normativ's setup params (the full import now refuses such a file).
const setupParamsOf = (row: ProdRow) =>
  (row.setupParams || []).filter((param) => param.description?.trim() || String(param.par ?? '').trim());

// Log Book по подпроцес (2026-10-08): one Excel row per stage — no machine column. The
// stage's machines are listed as read-only text; the import ignores that column. Rows of a
// stage are summed (a not-yet-migrated normativ can still hold one row per machine) and their
// setup params concatenated; operators = the highest count of the stage, the same rule as
// the server merge.
export function downloadOSProductivityExcel({
  rows,
  subprocesses,
  machines,
  razmerMachineIds = [],
  mainIdent = '',
  mainName = '',
}: ExportArgs) {
  const subprocessById = new Map(subprocesses.map((sp) => [sp._id, sp]));
  const machineById = new Map(machines.map((machine) => [machine._id, machine]));
  const groups = new Map<string, ProdRow[]>();
  rows
    .filter((row) =>
      row.subprocessId ||
      row.machineId ||
      row.montageTime ||
      row.machineTime ||
      row.cleaningTime ||
      row.dismountCleanTime ||
      row.operatorCount ||
      setupParamsOf(row).length > 0
    )
    .forEach((row) => {
      const key = row.subprocessId || '';
      groups.set(key, [...(groups.get(key) || []), row]);
    });

  const sumOf = (groupRows: ProdRow[], field: keyof ProdRow): number | '' => {
    const values = groupRows.filter((row) => String(row[field] ?? '').trim() !== '');
    return values.length > 0 ? values.reduce((sum, row) => sum + num(row[field]), 0) : '';
  };

  const data = Array.from(groups.entries()).map(([subprocessId, groupRows]) => {
    const isRazmer = subprocessId === RAZMER_STAGE_ID;
    const subprocess = subprocessId ? subprocessById.get(subprocessId) : undefined;
    const processDefinition =
      subprocess && typeof subprocess.processDefinition === 'object'
        ? subprocess.processDefinition
        : null;
    const stageMachines = (isRazmer ? razmerMachineIds : orderedMachineIds(subprocess?.machines))
      .map((id) => machineById.get(id))
      .filter((machine): machine is MachineOption => !!machine);
    const montage = sumOf(groupRows, 'montageTime');
    const machineTime = sumOf(groupRows, 'machineTime');
    const dismount = sumOf(groupRows, 'dismountCleanTime');
    const cleaning = sumOf(groupRows, 'cleaningTime');
    const operators = groupRows.map((row) => num(row.operatorCount)).filter((value) => value > 0);
    const setupParams = groupRows.flatMap(setupParamsOf);
    const total = [montage, machineTime, dismount, cleaning].reduce<number>((sum, value) => sum + (value || 0), 0);

    return {
      'Главна шифра': mainIdent,
      'Главен назив': mainName,
      'Тип на процес': isRazmer ? RAZMER_LABEL : (processDefinition?.name || ''),
      'Подпроцес': isRazmer ? RAZMER_LABEL : (subprocess?.name || ''),
      'Машини': stageMachines.map((machine) => machine.name || machine.code || '').filter(Boolean).join(', '),
      'Времетраење на монтажа': minutesToHoursText(montage),
      'Времетраење на машина': minutesToHoursText(machineTime),
      'Времетраење на демонтажа': minutesToHoursText(dismount),
      'Времетраење на чистење': minutesToHoursText(cleaning),
      'Број на оператори': operators.length > 0 ? Math.max(...operators) : '',
      'Парам. за подесување - Опис': setupParams.map((param) => param.description?.trim() || '').join('\n'),
      'Парам. за подесување - Пар': setupParams.map((param) => String(param.par ?? '').trim()).join('\n'),
      'Вкупно време': total > 0 ? minutesToHoursText(total) : '',
      'ЕМ': TIME_UNIT_TEXT,
    };
  });

  const headers = [
    'Главна шифра',
    'Главен назив',
    'Тип на процес',
    'Подпроцес',
    'Машини',
    'Времетраење на монтажа',
    'Времетраење на машина',
    'Времетраење на демонтажа',
    'Времетраење на чистење',
    'Број на оператори',
    'Парам. за подесување - Опис',
    'Парам. за подесување - Пар',
    'Вкупно време',
    'ЕМ',
  ];

  const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
  // One width per entry in `headers`, in the same order (mirrors the server-side export).
  worksheet['!cols'] = [
    { wch: 16 },  // Главна шифра
    { wch: 32 },  // Главен назив
    { wch: 24 },  // Тип на процес
    { wch: 24 },  // Подпроцес
    { wch: 32 },  // Машини (read-only)
    { wch: 20 },  // Времетраење на монтажа
    { wch: 20 },  // Времетраење на машина
    { wch: 20 },  // Времетраење на демонтажа
    { wch: 20 },  // Времетраење на чистење
    { wch: 18 },  // Број на оператори
    { wch: 28 },  // Парам. за подесување - Опис
    { wch: 16 },  // Парам. за подесување - Пар
    { wch: 14 },  // Вкупно време
    { wch: 10 },  // ЕМ
  ];
  worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(XLSX.utils.decode_range(worksheet['!ref'] || 'A1:N1')) };

  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:N1');
  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c });
    if (worksheet[cellRef]) worksheet[cellRef].s = headerStyle;
  }
  for (let r = 1; r <= range.e.r; r += 1) {
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = cellStyle;
        if (c === 9 && typeof worksheet[cellRef].v === 'number') {
          // Operators (col 9) are the only numeric column; the times are „H:MM“ text.
          worksheet[cellRef].t = 'n';
          worksheet[cellRef].z = '0.######';
        }
      }
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Продуктивност');
  XLSX.writeFile(
    workbook,
    `produktivnost_${safeFilenamePart(mainIdent)}_${new Date().toISOString().split('T')[0]}.xlsx`
  );
}
